import React, { useEffect, useState, useCallback } from 'react';
import {
  calendarService,
  TabletopAppointment,
  AvailableSlotsResponse,
  AvailableSlot,
} from '../../services/calendarService';

interface TabletopAppointmentSectionProps {
  exerciseId: number;
  exerciseName: string;
}

export const TabletopAppointmentSection: React.FC<TabletopAppointmentSectionProps> = ({
  exerciseId,
  exerciseName,
}) => {
  const [appointment, setAppointment] = useState<TabletopAppointment | null>(null);
  const [slotsData, setSlotsData] = useState<AvailableSlotsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [cancellingLoading, setCancellingLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Booking selection state
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null);
  const [clientNotes, setClientNotes] = useState<string>('');

  // Cancel modal state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [appt, slots] = await Promise.all([
        calendarService.getExerciseAppointment(exerciseId),
        calendarService.getAvailableSlots(exerciseId, 14),
      ]);
      setAppointment(appt);
      setSlotsData(slots);

      // Auto-select first date that has open slots
      if (slots && slots.days && slots.days.length > 0) {
        const firstDayWithSlots = slots.days.find((d) => d.slots && d.slots.length > 0);
        if (firstDayWithSlots) {
          setSelectedDate(firstDayWithSlots.date);
        } else {
          setSelectedDate(slots.days[0].date);
        }
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Unable to load appointment availability');
    } finally {
      setLoading(false);
    }
  }, [exerciseId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleBook = async () => {
    if (!selectedSlot) return;
    try {
      setBookingLoading(true);
      setError(null);
      const booked = await calendarService.bookAppointment(exerciseId, {
        start_time: selectedSlot.start_time,
        reviewer_id: selectedSlot.reviewer_id,
        client_notes: clientNotes.trim() || undefined,
      });
      setAppointment(booked);
      setSuccessMsg(`Review session scheduled with ${booked.reviewer_name}! A Google Meet link has been sent to your email.`);
      setSelectedSlot(null);
      setClientNotes('');
      // Reload slots to reflect updated availability
      const updatedSlots = await calendarService.getAvailableSlots(exerciseId, 14);
      setSlotsData(updatedSlots);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to book appointment. Please select another slot.');
    } finally {
      setBookingLoading(false);
    }
  };

  const handleCancel = async () => {
    try {
      setCancellingLoading(true);
      setError(null);
      await calendarService.cancelAppointment(exerciseId, cancelReason.trim() || undefined);
      setAppointment(null);
      setShowCancelModal(false);
      setCancelReason('');
      setSuccessMsg('Appointment cancelled. You can pick another time whenever you are ready.');
      // Refresh available slots
      const updatedSlots = await calendarService.getAvailableSlots(exerciseId, 14);
      setSlotsData(updatedSlots);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to cancel appointment');
    } finally {
      setCancellingLoading(false);
    }
  };

  const formatDisplayDate = (dateStr: string) => {
    try {
      const [year, month, day] = dateStr.split('-').map(Number);
      const d = new Date(year, month - 1, day);
      return d.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const formatFullDateTime = (dateStr: string) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short',
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-6 animate-pulse space-y-3">
        <div className="h-5 bg-slate-200 rounded w-1/3"></div>
        <div className="h-4 bg-slate-200 rounded w-2/3"></div>
        <div className="h-20 bg-slate-200 rounded"></div>
      </div>
    );
  }

  // CASE 1: APPOINTMENT ALREADY BOOKED
  if (appointment && appointment.status !== 'cancelled') {
    return (
      <div className="bg-gradient-to-br from-slate-900 to-[#0c1e40] text-white border border-slate-800 rounded-2xl p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
              Review Scheduled
            </div>
            <h3 className="text-lg font-bold text-white mt-1">
              Tabletop Review with {appointment.reviewer_name}
            </h3>
            <p className="text-xs text-slate-300">
              {appointment.reviewer_title || 'Lead Resilience Assessor & ISO 22301 Auditor'}
            </p>
          </div>

          {appointment.google_meet_link && (
            <a
              href={appointment.google_meet_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-xl shadow transition-colors"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14.5v-9l6 4.5-6 4.5z" />
              </svg>
              <span>Join Google Meet</span>
            </a>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-slate-800 grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
          <div>
            <span className="text-slate-400 block mb-0.5 font-medium">Date & Time</span>
            <span className="text-white font-semibold">{formatFullDateTime(appointment.start_time)}</span>
          </div>

          <div>
            <span className="text-slate-400 block mb-0.5 font-medium">Assessor Google Email</span>
            <span className="text-white font-semibold">{appointment.reviewer_email}</span>
          </div>
        </div>

        {appointment.client_notes && (
          <div className="mt-3 text-xs text-slate-300 bg-white/5 rounded-lg p-2.5 border border-white/10">
            <span className="font-semibold text-slate-200">Your notes: </span>
            {appointment.client_notes}
          </div>
        )}

        <div className="mt-4 pt-3 border-t border-slate-800 flex items-center justify-between text-xs">
          <span className="text-slate-400">Calendar invite sent to your email.</span>
          <button
            type="button"
            onClick={() => setShowCancelModal(true)}
            className="text-slate-400 hover:text-rose-300 font-semibold transition-colors underline cursor-pointer"
          >
            Reschedule or Cancel
          </button>
        </div>

        {/* Cancel Confirmation Modal */}
        {showCancelModal && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
            <div className="bg-white text-slate-900 rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4">
              <h4 className="text-base font-bold text-slate-900">Cancel Review Session?</h4>
              <p className="text-xs text-slate-600">
                This will release your time slot on {appointment.reviewer_name}&apos;s Google Calendar and cancel the Google Meet event. You can book a new time whenever you are ready.
              </p>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Reason for cancelling (optional):
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Need to reschedule, schedule conflict, etc."
                  rows={2}
                  className="w-full text-xs border border-slate-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCancelModal(false)}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg text-slate-600 hover:bg-slate-100"
                >
                  Keep Appointment
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={cancellingLoading}
                  className="px-4 py-1.5 text-xs font-bold rounded-lg bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50"
                >
                  {cancellingLoading ? 'Cancelling...' : 'Confirm Cancellation'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // CASE 2: NOT YET BOOKED — BOOKING UI
  const reviewer = slotsData?.reviewer;
  const currentDayData = slotsData?.days.find((d) => d.date === selectedDate);
  const openSlots = currentDayData?.slots || [];

  return (
    <div className="bg-white border border-slate-200/90 rounded-2xl p-6 shadow-2xs space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200/80 mb-1.5">
            <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Live Calendar Booking
          </div>
          <h3 className="text-base font-bold text-slate-900">
            Schedule Tabletop Review with {reviewer?.name || 'Rob'}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Select a live opening to walk through your tabletop results and ISO 22301 alignment over Google Meet.
          </p>
        </div>

        {reviewer?.avatar_url && (
          <img
            src={reviewer.avatar_url}
            alt={reviewer.name}
            className="w-10 h-10 rounded-full object-cover border border-slate-200"
          />
        )}
      </div>

      {/* Messages */}
      {error && (
        <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-800">
          {error}
        </div>
      )}

      {successMsg && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800">
          {successMsg}
        </div>
      )}

      {/* Date Picker Strip */}
      <div>
        <label className="block text-xs font-semibold text-slate-700 mb-2">
          Select Date ({slotsData?.timezone || 'America/Toronto'})
        </label>
        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-thin">
          {slotsData?.days.map((d) => {
            const hasSlots = d.slots && d.slots.length > 0;
            const isSelected = d.date === selectedDate;
            return (
              <button
                key={d.date}
                type="button"
                onClick={() => {
                  setSelectedDate(d.date);
                  setSelectedSlot(null);
                }}
                className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-medium border text-center transition-all ${
                  isSelected
                    ? 'bg-[#0c1e40] text-white border-[#0c1e40] shadow-xs'
                    : hasSlots
                    ? 'bg-white text-slate-800 border-slate-200 hover:border-slate-400'
                    : 'bg-slate-50 text-slate-400 border-slate-100 opacity-60 cursor-not-allowed'
                }`}
                disabled={!hasSlots}
              >
                <div className="font-bold">{formatDisplayDate(d.date)}</div>
                <div className={`text-[10px] mt-0.5 ${isSelected ? 'text-slate-200' : hasSlots ? 'text-blue-600 font-semibold' : 'text-slate-400'}`}>
                  {hasSlots ? `${d.slots.length} open` : 'No slots'}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Time Slots Grid */}
      <div>
        <label className="block text-xs font-semibold text-slate-700 mb-2">
          Available Times for {selectedDate ? formatDisplayDate(selectedDate) : 'Selected Day'}
        </label>
        {openSlots.length === 0 ? (
          <div className="p-4 bg-slate-50 border border-dashed border-slate-200 rounded-xl text-center text-xs text-slate-500">
            No remaining open slots on this day. Please pick another date above.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {openSlots.map((slot) => {
              const isSelected = selectedSlot?.start_time === slot.start_time;
              return (
                <button
                  key={slot.start_time}
                  type="button"
                  onClick={() => setSelectedSlot(slot)}
                  className={`px-3 py-2 text-xs font-semibold rounded-xl border text-center transition-all ${
                    isSelected
                      ? 'bg-blue-600 text-white border-blue-600 shadow-xs'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-blue-300 hover:bg-blue-50/50'
                  }`}
                >
                  {slot.formatted_time}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Optional Client Notes & Book Button */}
      {selectedSlot && (
        <div className="pt-3 border-t border-slate-100 space-y-3">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Notes for {reviewer?.name || 'Rob'} (optional)
            </label>
            <input
              type="text"
              value={clientNotes}
              onChange={(e) => setClientNotes(e.target.value)}
              placeholder="e.g. Focus on our third-party supplier recovery time"
              className="w-full text-xs border border-slate-300 rounded-xl p-2.5 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
          </div>

          <div className="flex items-center justify-between pt-1">
            <div className="text-xs text-slate-600">
              Selected: <strong className="text-slate-900">{formatDisplayDate(selectedDate)} at {selectedSlot.formatted_time}</strong>
            </div>
            <button
              type="button"
              onClick={handleBook}
              disabled={bookingLoading}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold bg-[#0c1e40] hover:bg-slate-800 text-white shadow-xs transition-colors disabled:opacity-50 cursor-pointer"
            >
              {bookingLoading ? (
                <>
                  <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                  Booking with Google Calendar...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>Confirm Review with {reviewer?.name || 'Rob'}</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TabletopAppointmentSection;
